import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { BookOpen, ArrowRight, Compass, Sparkles, Loader2, FileText } from "lucide-react";
import Reveal from "../components/Reveal";

/**
 * Learn — the curriculum landing.
 * Twelve "worlds of computing" laid out as pop-up-book pages.  Each world
 * card links deeper into that world, and every world is populated from the
 * imported chapter corpus (see /api/curriculum/worlds).
 */
export default function Learn() {
  const [data, setData] = useState(null);
  const [featured, setFeatured] = useState([]);
  useEffect(() => {
    api.get("/curriculum/worlds").then(({ data }) => setData(data)).catch(() => setData({ worlds: [], total: 0 }));
    api.get("/library?limit=6").then(({ data }) => setFeatured(data.articles || [])).catch(() => setFeatured([]));
  }, []);

  return (
    <div className="container-narrow py-14 md:py-20" data-testid="learn-page">
      {/* Hero */}
      <Reveal className="mb-12">
        <div className="inline-flex items-center gap-2 mb-3 text-xs uppercase tracking-[0.28em] font-bold text-marigold">
          <BookOpen className="w-3.5 h-3.5" /> The Curriculum
        </div>
        <h1 className="font-heading text-5xl md:text-6xl font-semibold text-ink leading-[1.05] mb-4 max-w-3xl">
          Twelve <span className="italic text-marigold gold-underline">worlds of computing</span>, in one long book.
        </h1>
        <p className="text-lg text-ink2 leading-relaxed max-w-2xl">
          Not a bootcamp. Not an LMS. An enormous interactive book — where each page
          is a chapter, each chapter has a home, and every home is somewhere on this
          map.
        </p>
        {data && (
          <div className="mt-4 text-sm text-ink2 font-mono">
            {data.total.toLocaleString()} chapters · {data.worlds.length} worlds · unrolling as you read
          </div>
        )}
      </Reveal>

      {/* World grid — pop-up book pages */}
      {!data ? (
        <div className="py-20 text-center text-ink2"><Loader2 className="w-6 h-6 mx-auto animate-spin" /></div>
      ) : (
        <div className="grid md:grid-cols-2 gap-5" data-testid="learn-worlds-grid">
          {data.worlds.map((w, i) => (
            <Reveal key={w.slug} delay={(i % 4) + 1}>
              <WorldCard world={w} idx={i} />
            </Reveal>
          ))}
        </div>
      )}

      {/* Featured chapters — imported PDF field notes */}
      {featured.length > 0 && (
        <Reveal className="mt-16" data-testid="learn-featured-section">
          <div className="flex items-baseline justify-between mb-5 flex-wrap gap-2">
            <div>
              <div className="text-[10px] uppercase tracking-[0.32em] font-bold text-marigold mb-2 inline-flex items-center gap-2">
                <FileText className="w-3 h-3" /> Fresh from the field notes
              </div>
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-ink">
                Peek inside the imported chapters.
              </h2>
            </div>
            <Link
              to="/library"
              className="text-xs uppercase tracking-widest font-bold text-ink2 hover:text-marigold inline-flex items-center gap-1"
              data-testid="learn-featured-see-all"
            >
              See the whole library <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="learn-featured-grid">
            {featured.map((a) => (
              <Link
                key={a.id || a.slug}
                to={`/library/${a.slug}`}
                className="block card-soft p-4 hover:-translate-y-0.5 hover:shadow-[6px_6px_0_0_#1F1A17] transition"
                data-testid={`learn-featured-${a.slug}`}
              >
                {a.book_title && (
                  <div className="text-[10px] font-mono text-marigold mb-1 line-clamp-1">{a.book_title}</div>
                )}
                <h3 className="font-heading text-base leading-tight mb-2 line-clamp-2">{a.title}</h3>
                {a.summary && (
                  <p className="text-xs text-ink2 mb-2 line-clamp-3">{a.summary}</p>
                )}
                <div className="flex items-center gap-2 text-[10px] font-mono text-ink2">
                  <span>{a.word_count} words</span>
                  {(a.tags || []).slice(0, 2).map((t) => (
                    <span key={t} className="px-1.5 py-0.5 rounded-full bg-marigold/10 text-marigold">{t}</span>
                  ))}
                </div>
              </Link>
            ))}
          </div>
        </Reveal>
      )}

      {/* Cross-link — atlas + adventures */}
      <Reveal className="mt-16">
        <div className="grid md:grid-cols-2 gap-5">
          <Link
            to="/learn/graph"
            className="group rounded-3xl border-2 border-ink bg-cream shadow-[6px_6px_0_0_#1F1A17] hover:-translate-y-1 hover:shadow-[10px_10px_0_0_#1F1A17] transition-all p-8 flex items-center gap-4 flex-wrap"
            data-testid="learn-graph-cta"
          >
            <div className="w-14 h-14 rounded-2xl bg-teal/25 text-teal flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-6 h-6" />
            </div>
            <div className="flex-1 min-w-[200px]">
              <div className="text-[11px] uppercase tracking-[0.28em] font-bold text-marigold mb-1">
                The Constellation
              </div>
              <h3 className="font-heading text-2xl font-semibold text-ink leading-tight group-hover:text-marigold transition-colors">
                See how the twelve worlds touch.
              </h3>
              <p className="text-sm text-ink2 mt-1">
                Recursion → trees → compilers in one glance. A hand-drawn concept map.
              </p>
            </div>
            <ArrowRight className="w-5 h-5 text-ink group-hover:translate-x-1 transition-transform" />
          </Link>

          <Link
            to="/adventures"
            className="group rounded-3xl border-2 border-ink bg-cream shadow-[6px_6px_0_0_#1F1A17] hover:-translate-y-1 hover:shadow-[10px_10px_0_0_#1F1A17] transition-all p-8 flex items-center gap-4 flex-wrap"
            data-testid="learn-adventures-cta"
          >
            <div className="w-14 h-14 rounded-2xl bg-marigold/25 text-marigold flex items-center justify-center flex-shrink-0">
              <Compass className="w-6 h-6" />
            </div>
            <div className="flex-1 min-w-[200px]">
              <div className="text-[11px] uppercase tracking-[0.28em] font-bold text-marigold mb-1">
                Adventures
              </div>
              <h3 className="font-heading text-2xl font-semibold text-ink leading-tight group-hover:text-marigold transition-colors">
                Or learn by going somewhere.
              </h3>
              <p className="text-sm text-ink2 mt-1">
                Story-shaped tours — chapters threaded through a single plot. Start with <em>The Lost Internet</em>.
              </p>
            </div>
            <ArrowRight className="w-5 h-5 text-ink group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </Reveal>
    </div>
  );
}

function WorldCard({ world, idx }) {
  const pop = ["-rotate-1", "rotate-1", "-rotate-[0.5deg]", "rotate-[0.5deg]"][idx % 4];
  return (
    <Link
      to={`/learn/${world.slug}`}
      className={`group relative block rounded-3xl border-2 border-ink bg-cream p-6 md:p-7 shadow-[6px_6px_0_0_#1F1A17] hover:-translate-y-1 hover:shadow-[10px_10px_0_0_#1F1A17] transition-all overflow-hidden ${pop}`}
      data-testid={`learn-world-${world.slug}`}
    >
      {/* Chapter number in the corner — pop-up-book style */}
      <div className="absolute top-3 right-4 font-heading text-6xl md:text-7xl font-semibold text-ink/[0.06] leading-none select-none pointer-events-none">
        {String(idx + 1).padStart(2, "0")}
      </div>

      <div className="text-[10px] uppercase tracking-[0.32em] font-bold text-marigold mb-2 relative">
        {world.kicker}
      </div>
      <h3 className={`font-heading text-2xl md:text-3xl font-semibold ${world.accent || "text-ink"} leading-tight mb-3 relative`}>
        {world.title}
      </h3>
      <p className="text-sm text-ink2 italic leading-relaxed mb-5 relative">
        {world.one_line}
      </p>

      <div className="flex items-center justify-between text-xs relative">
        <span className="text-ink2 font-mono">
          {world.count.toLocaleString()} {world.count === 1 ? "chapter" : "chapters"}
        </span>
        <span className="inline-flex items-center gap-1 font-bold text-ink group-hover:text-marigold transition-colors">
          Enter <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
        </span>
      </div>
    </Link>
  );
}
